
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, Upload, Trash2, MoveUp, MoveDown, Eye } from 'lucide-react';
import { useCarouselImages } from '@/hooks/useCarouselImages';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CarouselImage {
  id: string;
  file_path: string;
  alt_text: string;
  sort_order: number;
  is_active: boolean;
}

const CarouselManager = () => {
  const { images, loading, refetch } = useCarouselImages();
  const { toast } = useToast();
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newImageAlt, setNewImageAlt] = useState('');

  const handleFileUpload = async (file: File) => {
    try {
      setUploadingFile(true);
      
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `carousel-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('homepage-carousel')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('homepage-carousel')
        .getPublicUrl(filePath);

      // Get next sort order
      const maxSortOrder = Math.max(...images.map(img => img.sort_order), 0);

      // Save to database
      const { error: dbError } = await supabase
        .from('carousel_images')
        .insert({
          file_path: publicUrl,
          alt_text: newImageAlt || `Carousel image ${maxSortOrder + 1}`,
          sort_order: maxSortOrder + 1,
          is_active: true
        });

      if (dbError) throw dbError;

      toast({ title: '图片上传成功' });
      setNewImageAlt('');
      refetch();
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: '图片上传失败', variant: 'destructive' });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDelete = async (imageId: string) => {
    try {
      const { error } = await supabase
        .from('carousel_images')
        .delete()
        .eq('id', imageId);

      if (error) throw error;

      toast({ title: '图片删除成功' });
      refetch();
    } catch (error) {
      console.error('Delete error:', error);
      toast({ title: '图片删除失败', variant: 'destructive' });
    }
  };

  const handleToggleActive = async (imageId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('carousel_images')
        .update({ is_active: !isActive })
        .eq('id', imageId);

      if (error) throw error;

      toast({ title: '状态更新成功' });
      refetch();
    } catch (error) {
      console.error('Toggle error:', error);
      toast({ title: '状态更新失败', variant: 'destructive' });
    }
  };

  const handleReorder = async (imageId: string, direction: 'up' | 'down') => {
    try {
      const currentImage = images.find(img => img.id === imageId);
      if (!currentImage) return;

      const sortedImages = [...images].sort((a, b) => a.sort_order - b.sort_order);
      const currentIndex = sortedImages.findIndex(img => img.id === imageId);
      
      if (direction === 'up' && currentIndex > 0) {
        const swapImage = sortedImages[currentIndex - 1];
        await Promise.all([
          supabase.from('carousel_images').update({ sort_order: swapImage.sort_order }).eq('id', imageId),
          supabase.from('carousel_images').update({ sort_order: currentImage.sort_order }).eq('id', swapImage.id)
        ]);
      } else if (direction === 'down' && currentIndex < sortedImages.length - 1) {
        const swapImage = sortedImages[currentIndex + 1];
        await Promise.all([
          supabase.from('carousel_images').update({ sort_order: swapImage.sort_order }).eq('id', imageId),
          supabase.from('carousel_images').update({ sort_order: currentImage.sort_order }).eq('id', swapImage.id)
        ]);
      }

      toast({ title: '排序更新成功' });
      refetch();
    } catch (error) {
      console.error('Reorder error:', error);
      toast({ title: '排序更新失败', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="h-5 w-5" />
          首页轮播图管理
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Section */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="alt-text">图片描述</Label>
            <Input
              id="alt-text"
              value={newImageAlt}
              onChange={(e) => setNewImageAlt(e.target.value)}
              placeholder="输入图片描述..."
            />
          </div>
          <div>
            <Label htmlFor="file-upload">上传新图片</Label>
            <Input
              id="file-upload"
              type="file"
              accept="image/*"
              disabled={uploadingFile}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
              }}
            />
          </div>
        </div>

        {/* Images List */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">当前轮播图片</h3>
          {loading ? (
            <div className="text-center py-4">加载中...</div>
          ) : images.length === 0 ? (
            <div className="text-center py-4 text-gray-500">暂无轮播图片</div>
          ) : (
            <div className="space-y-2">
              {images
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((image, index) => (
                  <div key={image.id} className="flex items-center gap-4 p-4 border rounded-lg">
                    <img 
                      src={image.file_path} 
                      alt={image.alt_text}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{image.alt_text}</p>
                      <p className="text-sm text-gray-500">排序: {image.sort_order}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={image.is_active}
                        onCheckedChange={() => handleToggleActive(image.id, image.is_active)}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReorder(image.id, 'up')}
                        disabled={index === 0}
                      >
                        <MoveUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReorder(image.id, 'down')}
                        disabled={index === images.length - 1}
                      >
                        <MoveDown className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(image.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CarouselManager;
